/**
 * External dependencies
 */
import Uppy from '@uppy/core';
import XHRUpload from '@uppy/xhr-upload';

const ALLOWED_FILE_TYPES = [
	'.pdf', '.doc', '.docx', '.xls', '.xlsx',
	'.jpg', '.jpeg', '.png', '.gif', '.txt',
];

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB

const MAX_NUMBER_FILES = 10;

const wdgRfp = window.wdgRfp || {};

let turnstileLoader = null;

/**
 * Loads the Turnstile script once.
 *
 * @return {Promise<Object>} Resolves with the Turnstile API.
 */
function loadTurnstile() {
	if ( ! turnstileLoader ) {
		turnstileLoader = new Promise( ( resolve, reject ) => {
			const script = document.createElement( 'script' );
			script.src = wdgRfp?.turnstileUrl;
			script.addEventListener( 'load', () => resolve( window.turnstile ) );
			script.addEventListener( 'error', () => {
				turnstileLoader = null;
				reject( new Error( 'Turnstile failed to load.' ) );
			} );
			document.head.appendChild( script );
		} );
	}

	return turnstileLoader;
}

export class RFPBlock {
	static instance = null;

	/**
	 * Open the modal from any context (e.g. button click, other UI triggers)
	 * @param {HTMLElement} blockEl Optional block element to set context
	 */
	static open( blockEl ) {
		if ( ! RFPBlock.instance ) {
			RFPBlock.instance = new RFPBlock();
		}

		if ( blockEl ) {
			RFPBlock.instance.setBlockContext( blockEl );
		}

		RFPBlock.instance.dialog.showModal();
		document.body.classList.add( 'wdg-rfp-open' );
	}

	constructor() {
		this.initialized = false;
		this.dialog = document.getElementById( 'wdg-rfp-dialog' );
		this.form = this.dialog.querySelector( '.wdg-rfp-dialog-form' );
		this.submitBtn = this.dialog.querySelector( 'button[type="submit"]' );
		this.content = this.dialog.querySelector( '.wdg-rfp-dialog-content' );
		this.confirmation = this.dialog.querySelector( '.wdg-rfp-dialog-confirmation' );
		this.fileList = this.dialog.querySelector( '.wdg-rfp-dialog-file-list' );
		this.fileElements = new Map();
		this.gfEntryId = null;
		this.gfToken = null;
		this.uploadedFiles = [];
		this.errors = [];
		this.uploadingCount = 0;
		this.submitting = false;
		this.verifyDialog = document.getElementById( 'wdg-rfp-verify-dialog' );
		this.verifyWidget = this.verifyDialog.querySelector( '.wdg-rfp-verify-dialog-widget' );
		this.verifyError = this.verifyDialog.querySelector( '.wdg-rfp-verify-dialog-error' );
		this.verifyStatus = this.verifyDialog.querySelector( '.wdg-rfp-verify-dialog-status' );
		this.verifyContinue = this.verifyDialog.querySelector( '.wdg-rfp-verify-dialog-continue' );
		this.verifyLimited = this.verifyDialog.querySelector( '.wdg-rfp-verify-dialog-limited' );
		this.turnstileContainer = document.querySelector( '[data-rfp-turnstile]' );
		this.widgetId = null;
		this.loadingTurnstile = false;
		this.turnstileToken = null;
		this.creatingEntry = false;
		this.retriedCreate = false;
		this.engaged = false;
		this.awaitingPicker = false;
		this.needsInteraction = false;
		this.pending = false;
		this.triggers = [ ...document.querySelectorAll( '[data-rfp-input-trigger]' ) ].filter( ( el ) => ! this.dialog.contains( el ) );

		this.initInput();
		this.initDialogEvents();
		this.initVerifyDialogEvents();
		this.initUppy();
		this.form.addEventListener( 'submit', this.handleSubmit.bind( this ) );
	}

	setBlockContext( blockEl ) {
		this.block = blockEl;
		this.xhrEndpoint = this.block.dataset.targetUri;
	}

	initInput() {
		this.fileInput = document.createElement( 'input' );
		this.fileInput.type = 'file';
		this.fileInput.multiple = true;
		this.fileInput.accept = ALLOWED_FILE_TYPES.join( ',' );
		this.fileInput.style.display = 'none';
		document.body.appendChild( this.fileInput );

		this.fileInput.addEventListener( 'change', ( e ) => {
			const files = e.target.files;
			if ( ! files ) {
				return;
			}

			this.addFiles( files, 'Local' );

			this.fileInput.value = '';
		} );
	}

	/**
	 * Adds files to Uppy and starts the upload flow.
	 *
	 * @param {FileList} files  Files to add.
	 * @param {string}   source Uppy source label.
	 */
	addFiles( files, source ) {
		for ( const file of files ) {
			try {
				this.uppy.addFile( {
					name: file.name,
					type: file.type,
					data: file,
					source,
				} );
			} catch ( err ) {
				// Restriction failures are reported through the restriction-failed event.
			}
		}

		this.start();
	}

	/**
	 * Uploads queued files when an entry exists, otherwise waits for a Turnstile token to create one.
	 */
	start() {
		this.engaged = true;

		if ( this.gfEntryId ) {
			this.uppy.upload();
			return;
		}

		if ( ! this.uppy.getFiles().length ) {
			if ( this.errors.length ) {
				this.openDialog();
			}
			return;
		}

		if ( ! wdgRfp?.turnstileKey ) {
			this.maybeCreateEntry();
			return;
		}

		if ( this.needsInteraction ) {
			this.showChallenge();
			return;
		}

		this.prepareVerification();
		this.maybeCreateEntry();
	}

	/**
	 * Opens the file picker when Turnstile is inactive or a token is ready, otherwise shows the verification dialog until one is.
	 */
	requestPicker() {
		this.engaged = true;

		if ( ! wdgRfp?.turnstileKey || this.turnstileToken ) {
			this.fileInput.click();
			return;
		}

		this.awaitingPicker = true;

		if ( this.needsInteraction ) {
			this.showChallenge();
			return;
		}

		this.setVerifyState( 'checking' );
		this.openVerifyDialog();
		this.prepareVerification();
	}

	openDialog() {
		if ( ! this.dialog.open ) {
			this.dialog.showModal();
			document.body.classList.add( 'wdg-rfp-open' );
		}
	}

	initVerifyDialogEvents() {
		this.verifyDialog.querySelector( '.wdg-rfp-dialog-close' )?.addEventListener( 'click', () => {
			this.cancelVerification();
		} );

		this.verifyDialog.addEventListener( 'cancel', ( e ) => {
			e.preventDefault();
			this.cancelVerification();
		} );

		this.verifyDialog.querySelector( '.wdg-rfp-verify-dialog-retry' )?.addEventListener( 'click', () => {
			this.showChallenge();
		} );

		this.verifyContinue.addEventListener( 'click', () => {
			this.awaitingPicker = false;
			this.verifyDialog.close();
			this.fileInput.click();
		} );
	}

	/**
	 * Shows one of the verification dialog's states: checking, challenge, ready, error or limited.
	 *
	 * @param {string} state Dialog state.
	 */
	setVerifyState( state ) {
		this.verifyStatus.hidden = 'checking' !== state;
		this.verifyContinue.hidden = 'ready' !== state;
		this.verifyError.hidden = 'error' !== state;
		this.verifyLimited.hidden = 'limited' !== state;
	}

	/**
	 * Discards queued files and shows the rate limit message in the verification dialog.
	 */
	showRateLimited() {
		this.reset();
		this.setVerifyState( 'limited' );
		this.openVerifyDialog();
	}

	/**
	 * Loads Turnstile and renders the hidden widget, once per entry.
	 */
	prepareVerification() {
		if ( null !== this.widgetId || this.loadingTurnstile || this.turnstileToken || this.needsInteraction ) {
			return;
		}

		this.loadingTurnstile = true;
		this.setPending( true );

		loadTurnstile()
			.then( () => this.renderWidget( this.turnstileContainer, true ) )
			.catch( () => this.showVerifyError() )
			.finally( () => {
				this.loadingTurnstile = false;
			} );
	}

	/**
	 * Marks triggers outside the RFP dialog as pending while the Turnstile check runs.
	 *
	 * @param {boolean} pending Whether the check is running.
	 */
	setPending( pending ) {
		this.pending = pending;

		this.triggers.forEach( ( el ) => {
			const target = el.querySelector( 'button' ) || el;

			el.classList.toggle( 'is-pending', pending );

			if ( pending ) {
				target.setAttribute( 'aria-disabled', 'true' );
			} else {
				target.removeAttribute( 'aria-disabled' );
			}
		} );
	}

	/**
	 * Renders a Turnstile widget.
	 *
	 * @param {HTMLElement} container Element to render into.
	 * @param {boolean}     hidden    Whether the widget stays invisible unless Cloudflare requires interaction.
	 */
	renderWidget( container, hidden ) {
		const options = {
			sitekey: wdgRfp?.turnstileKey,
			action: wdgRfp?.turnstileAction,
			appearance: 'always',
			callback: ( token ) => this.onToken( token ),
			'error-callback': () => {
				this.showVerifyError();
				return true;
			},
			'timeout-callback': () => this.showVerifyError(),
			'expired-callback': () => {
				this.turnstileToken = null;
				this.setPending( true );
			},
		};

		if ( hidden ) {
			options.appearance = 'interaction-only';
			options[ 'before-interactive-callback' ] = () => this.onInteractive();
		}

		this.widgetId = window.turnstile.render( container, options );
	}

	removeWidget() {
		if ( null !== this.widgetId ) {
			window.turnstile.remove( this.widgetId );
			this.widgetId = null;
		}
	}

	onToken( token ) {
		this.turnstileToken = token;
		this.setPending( false );

		if ( this.awaitingPicker ) {
			this.setVerifyState( 'ready' );
			this.openVerifyDialog();
		}

		this.maybeCreateEntry();
	}

	/**
	 * Removes the hidden widget when Cloudflare requires interaction, and shows the challenge once the visitor has engaged.
	 */
	onInteractive() {
		this.removeWidget();
		this.turnstileToken = null;
		this.needsInteraction = true;
		this.setPending( false );

		if ( this.engaged ) {
			this.showChallenge();
		}
	}

	/**
	 * Renders a visible widget in the verification dialog.
	 */
	showChallenge() {
		this.needsInteraction = false;
		this.removeWidget();
		this.setVerifyState( 'challenge' );
		this.openVerifyDialog();

		loadTurnstile()
			.then( () => this.renderWidget( this.verifyWidget, false ) )
			.catch( () => this.showVerifyError() );
	}

	openVerifyDialog() {
		if ( ! this.verifyDialog.open ) {
			this.verifyDialog.showModal();
		}
	}

	/**
	 * Clears the widget and token, and shows the error once the visitor has engaged.
	 */
	showVerifyError() {
		this.removeWidget();
		this.turnstileToken = null;
		this.creatingEntry = false;
		this.setPending( false );

		if ( ! this.engaged ) {
			return;
		}

		this.setVerifyState( 'error' );
		this.openVerifyDialog();
	}

	/**
	 * Closes the verification dialog and discards queued files.
	 */
	cancelVerification() {
		this.verifyDialog.close();
		this.reset();
	}

	/**
	 * Creates the entry once at least one file is queued and, while Turnstile is active, a token is present.
	 */
	maybeCreateEntry() {
		if ( ( wdgRfp?.turnstileKey && ! this.turnstileToken ) || this.gfEntryId || this.creatingEntry || ! this.uppy.getFiles().length ) {
			return;
		}

		this.creatingEntry = true;
		this.createEntry( this.turnstileToken );
		this.turnstileToken = null;
	}

	/**
	 * Posts to create an entry, with the Turnstile token when there is one. A 403 renders a new hidden widget and retries once before showing an error. A 429 shows the rate limit message.
	 *
	 * @param {?string} token Turnstile token.
	 */
	createEntry( token ) {
		const body = new URLSearchParams( {
			action: 'wdg_rfp_create',
		} );

		if ( token ) {
			body.set( 'turnstile', token );
		}

		fetch( wdgRfp?.ajaxUrl, {
			method: 'POST',
			body,
		} )
			.then( ( res ) => res.json().then( ( result ) => ( {
				status: res.status,
				result,
			} ) ) )
			.then( ( { status, result } ) => {
				this.creatingEntry = false;

				if ( result.success ) {
					this.onEntryCreated( result.data );
				} else if ( 403 === status && ! this.retriedCreate ) {
					this.retriedCreate = true;
					this.removeWidget();
					this.renderWidget( this.turnstileContainer, true );
				} else if ( 403 === status ) {
					this.showVerifyError();
				} else if ( 429 === status ) {
					this.showRateLimited();
				} else {
					throw new Error( result?.data || 'Server error' );
				}
			} )
			.catch( ( err ) => {
				this.creatingEntry = false;
				// eslint-disable-next-line no-alert
				alert( err.message || 'An error occurred while creating the RFP entry.' );
				this.reset();
			} );
	}

	onEntryCreated( { entryId, token } ) {
		this.gfEntryId = entryId;
		this.gfToken = token;
		this.removeWidget();

		if ( this.verifyDialog.open ) {
			this.verifyDialog.close();
		}

		this.openDialog();
		this.uppy.upload();
	}

	initDialogEvents() {
		this.dialog.querySelector( '.wdg-rfp-dialog-close' )?.addEventListener( 'click', () => {
			this.dialog.close();
			this.showContent();
			this.reset();
		} );

		this.dialog.addEventListener( 'close', () => {
			this.submitting = false;
			this.updateSubmitState();

			document.body.classList.remove( 'wdg-rfp-open' );
		} );

		this.dialog.addEventListener( 'cancel', ( e ) => {
			e.preventDefault();
			this.dialog.close();
			this.showContent();
			this.reset();
		} );
	}

	initUppy() {
		this.uppy = new Uppy( {
			id: 'wdg-rfp-uppy',
			autoProceed: false,
			restrictions: {
				maxFileSize: MAX_FILE_SIZE,
				allowedFileTypes: ALLOWED_FILE_TYPES,
				maxNumberOfFiles: MAX_NUMBER_FILES,
			},
			onBeforeUpload: ( files ) => {
				this.updateSubmitState();

				return files;
			},
		} );

		this.uppy.use( XHRUpload, {
			endpoint: wdgRfp?.ajaxUrl,
			formData: true,
			bundle: false,
			limit: 1,
			onBeforeRequest: ( req ) => {
				if ( this.gfEntryId && this.gfToken ) {
					if ( typeof req.setRequestHeader === 'function' ) {
						req.setRequestHeader( 'X-GF-Entry-ID', this.gfEntryId );
						req.setRequestHeader( 'X-GF-Token', this.gfToken );
					} else if ( req.headers && typeof req.headers.set === 'function' ) {
						req.headers.set( 'X-GF-Entry-ID', this.gfEntryId );
						req.headers.set( 'X-GF-Token', this.gfToken );
					} else if ( req.meta && req.meta.headers ) {
						req.meta.headers[ 'X-GF-Entry-ID' ] = this.gfEntryId;
						req.meta.headers[ 'X-GF-Token' ] = this.gfToken;
					}
				}

				return req;
			},
			shouldRetry: () => false,
		} );

		this.uppy.setMeta( { action: 'wdg_rfp_upload' } );

		this.uppy.on( 'file-added', this.onFileAdded.bind( this ) );
		this.uppy.on( 'upload-progress', this.onUploadProgress.bind( this ) );
		this.uppy.on( 'upload-success', this.onUploadSuccess.bind( this ) );
		this.uppy.on( 'restriction-failed', this.onRestrictionFailed.bind( this ) );
		this.uppy.on( 'upload-error', this.onUploadError.bind( this ) );
	}

	handleSubmit( e ) {
		e.preventDefault();

		if ( ! this.canSubmit() ) {
			// eslint-disable-next-line no-alert
			alert( 'Please resolve any errors or wait for uploads to finish.' );
			return;
		}

		const formData = new FormData( this.form );
		formData.append( 'action', 'wdg_rfp_form_submit' );

		this.submitting = true;
		this.updateSubmitState();

		fetch( wdgRfp?.ajaxUrl, {
			method: 'POST',
			body: formData,
			headers: {
				'X-GF-Entry-ID': this.gfEntryId || '',
				'X-GF-Token': this.gfToken || '',
			},
		} )
			.then( ( res ) => res.json() )
			.then( ( result ) => {
				if ( result.success ) {
					this.showConfirmation();
					this.reset();
				} else {
					throw new Error( result?.data || 'Server error' );
				}
			} )
			.catch( ( err ) => {
				// eslint-disable-next-line no-alert
				alert( err.message || 'An error occurred while submitting the form.' );
			} )
			.finally( () => {
				this.submitting = false;
				this.updateSubmitState();
			} );
	}

	reset() {
		this.form?.reset();
		this.gfEntryId = null;
		this.gfToken = null;
		this.uploadedFiles = [];
		this.errors = [];
		this.uploadingCount = 0;
		this.fileList.innerHTML = '';
		this.fileElements.clear();
		this.updateSubmitState();
		this.uppy.clear();
		this.removeWidget();
		this.turnstileToken = null;
		this.creatingEntry = false;
		this.retriedCreate = false;
		this.engaged = false;
		this.awaitingPicker = false;
		this.needsInteraction = false;
		this.setVerifyState( 'checking' );

		if ( wdgRfp?.turnstileKey ) {
			this.prepareVerification();
		}
	}

	renderFileItem( file, { status = 'loading', errorMessage = '' } = {} ) {
		const template = document.getElementById( 'wdg-rfp-dialog-file-template' );
		if ( ! template?.content ) {
			return;
		}

		const item = template.content.cloneNode( true ).firstElementChild;
		item.dataset.fileId = file.id;
		item.querySelector( '.wdg-rfp-dialog-file__name' ).textContent = file.name;
		item.classList.add( `is-${ status }` );

		const percentEl = item.querySelector( '.wdg-rfp-dialog-file__percent' );
		if ( status === 'error' ) {
			this.errors.push( file.id );
			if ( percentEl ) {
				percentEl.textContent = errorMessage || 'Error';
			}
		} else if ( status === 'loading' ) {
			if ( percentEl ) {
				percentEl.textContent = '0%';
			}
		} else {
			percentEl?.remove();
		}

		item.querySelector( '.wdg-rfp-dialog-file__remove' )?.addEventListener( 'click', () => {
			item.remove();
			this.uppy.removeFile( file.id );
			this.uploadedFiles = this.uploadedFiles.filter( ( f ) => f.id !== file.id );
			this.errors = this.errors.filter( ( id ) => id !== file.id );
			this.fileElements.delete( file.id );
			this.updateSubmitState();
		} );

		this.fileList.appendChild( item );
		this.fileElements.set( file.id, item );
		this.updateSubmitState();
	}

	onFileAdded( file ) {
		this.uploadingCount++;

		this.renderFileItem( file, { status: 'loading' } );
		this.updateSubmitState();
	}

	onUploadProgress( file, progress ) {
		const item = this.fileElements.get( file.id );
		if ( ! item ) {
			return;
		}
		const percent = Math.round( ( progress.bytesUploaded / progress.bytesTotal ) * 100 );
		item.querySelector( '.wdg-rfp-dialog-file__percent' ).textContent = `${ percent }%`;
	}

	onUploadSuccess( file, response ) {
		this.uploadingCount--;
		const item = this.fileElements.get( file.id );
		if ( item ) {
			item.classList.remove( 'is-loading' );
			item.classList.add( 'is-success' );
			item.querySelector( '.wdg-rfp-dialog-file__percent' )?.remove();
		}
		this.uploadedFiles.push( { id: file.id, name: file.name, type: file.type, url: response.uploadURL } );
		this.updateSubmitState();
	}

	onUploadError( file, error ) {
		this.uploadingCount = Math.max( 0, this.uploadingCount - 1 );

		if ( ! this.errors.includes( file.id ) ) {
			this.errors.push( file.id );
		}

		const item = this.fileElements.get( file.id );
		if ( item ) {
			item.classList.remove( 'is-loading' );
			item.classList.add( 'is-error' );
			const percentEl = item.querySelector( '.wdg-rfp-dialog-file__percent' );
			if ( percentEl ) {
				percentEl.textContent = error?.message || 'Upload failed';
			}
		}
		this.updateSubmitState();
	}

	onRestrictionFailed( file, error ) {
		const fileId = `restriction-${ file.name }-${ Date.now() }`;

		// Don’t double-add
		if ( ! this.errors.includes( fileId ) ) {
			this.errors.push( fileId );
		}

		// Render an error item if not already shown
		if ( ! this.fileElements.has( fileId ) ) {
			this.renderFileItem(
				{
					id: fileId,
					name: file.name || 'Invalid File',
					type: file.type || 'Unknown',
				},
				{ status: 'error', errorMessage: error?.message || 'Invalid file type' }
			);
		}

		this.updateSubmitState();
	}

	canSubmit() {
		return (
			this.uploadingCount === 0 &&
			this.errors.length === 0 &&
			this.uploadedFiles.length > 0 &&
			! this.submitting
		);
	}

	updateSubmitState() {
		const disabled = ! this.canSubmit();
		if ( this.submitBtn ) {
			this.submitBtn.disabled = disabled;
			this.submitBtn.textContent = this.submitting ? 'Sending…' : 'Send';
		}
	}

	showContent() {
		this.content.style.display = 'contents';
		this.confirmation.style.display = 'none';
	}

	showConfirmation() {
		this.content.style.display = 'none';
		this.confirmation.style.display = 'block';
	}
}

document.addEventListener( 'DOMContentLoaded', () => {
	/**
	 * Bind to click events on elements with data-rfp-input-trigger
	 */
	document.querySelectorAll( '[data-rfp-input-trigger]' ).forEach( ( el ) => {
		el.addEventListener( 'click', ( e ) => {
			e.preventDefault();
			if ( ! RFPBlock.instance ) {
				RFPBlock.instance = new RFPBlock();
			}
			const block = el.closest( '.wp-block-wdg-rfp' );
			if ( block ) {
				RFPBlock.instance.setBlockContext( block );
			}
			if ( RFPBlock.instance.dialog.contains( el ) ) {
				RFPBlock.instance.fileInput.click();
			} else if ( ! RFPBlock.instance.pending ) {
				RFPBlock.instance.requestPicker();
			}
		} );
	} );

	/**
	 * Start verification on page load when a trigger exists outside the RFP dialog
	 */
	const hasTrigger = [ ...document.querySelectorAll( '[data-rfp-input-trigger]' ) ].some( ( el ) => ! el.closest( '#wdg-rfp-dialog' ) );

	if ( hasTrigger && wdgRfp?.turnstileKey ) {
		if ( ! RFPBlock.instance ) {
			RFPBlock.instance = new RFPBlock();
		}
		RFPBlock.instance.prepareVerification();
	}

	/**
	 * Bind drag & drop events to elements with data-rfp-drop-target
	 */
	document.querySelectorAll( '[data-rfp-drop-target]' ).forEach( ( target ) => {
		[ 'dragenter', 'dragover', 'dragleave', 'drop' ].forEach( ( evt ) => {
			target.addEventListener( evt, ( e ) => {
				e.preventDefault();
				e.stopPropagation();
				if ( evt === 'dragenter' || evt === 'dragover' ) {
					target.classList.add( 'is-dragover' );
				} else {
					target.classList.remove( 'is-dragover' );
				}
			} );
		} );

		target.addEventListener( 'drop', ( e ) => {
			e.preventDefault();
			e.stopPropagation();

			if ( ! RFPBlock.instance ) {
				RFPBlock.instance = new RFPBlock();
			}

			const block = target.closest( '.wp-block-wdg-rfp' );
			if ( block ) {
				RFPBlock.instance.setBlockContext( block );
			}

			if ( e.dataTransfer?.files ) {
				RFPBlock.instance.addFiles( e.dataTransfer.files, 'Drop' );
			}
		} );
	} );
} );
